<template>
  <section id="hero" class="relative flex items-center justify-center overflow-hidden hero-section" ref="heroSection" @mousemove="moveCursor" @mouseenter="showCursor" @mouseleave="hideCursor">
    <div class="absolute inset-0">
      <video 
        ref="backgroundVideoRef"
        :src="backgroundVideo"
        autoplay
        loop
        muted
        playsinline
        preload="auto"
        class="w-full h-full object-cover"
        @loadedmetadata="handleVideoLoaded"
        @error="handleVideoError"
      ></video>
      <div class="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50"></div>
    </div>
    
    <div class="relative z-10 text-center px-4 max-w-5xl mx-auto" ref="heroContent">
      <h1 class="hero-title font-heading tracking-tight text-white">{{ $t('hero.title') }}</h1>
      <p class="hero-subtitle text-lg sm:text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-8 text-justify">{{ $t('hero.subtitle') }}</p>
      <div 
        v-if="showSliderText" 
        class="hero-slider-text text-lg sm:text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mt-8"
      >
        {{ displayedSliderText }}<span class="typing-cursor">|</span>
      </div>
     </div>
    
    <!-- Scroll Indicator on Left Side -->
    <div 
      class="scroll-indicator-left fixed left-8 top-1/2 -translate-y-1/2 z-20 cursor-pointer"
      @click="scrollToNext"
    >
      <div class="scroll-indicator-line"></div>
      <div class="scroll-indicator-arrow">
        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </div>
    </div>
    
    <div v-show="cursorVisible" ref="customCursor" class="custom-cursor"></div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { textReveal } from '../utils/animations'
// Using MP4 version for better web compatibility (3840x2160 - still very high quality)
import backgroundVideo from '../assets/1478243_People_Technology_3840x2160.mp4'

const { t } = useI18n()
const heroSection = ref(null)
const heroContent = ref(null)
const backgroundVideoRef = ref(null)
const showSliderText = ref(false)
const displayedSliderText = ref('')

// Custom cursor logic
const customCursor = ref(null)
const cursorVisible = ref(false)
function moveCursor(e) {
  if (customCursor.value) {
    customCursor.value.style.left = e.clientX + 'px'
    customCursor.value.style.top = e.clientY + 'px'
  }
}
function showCursor() {
  cursorVisible.value = true
}
function hideCursor() {
  cursorVisible.value = false
}

const handleVideoLoaded = () => {
  console.log('Video loaded successfully')
  if (backgroundVideoRef.value) {
    backgroundVideoRef.value.play().catch(error => {
      console.log('Video autoplay prevented:', error)
    })
  }
}

const handleVideoError = (e) => {
  console.error('Video loading error:', e)
  console.log('Video source:', backgroundVideo)
}

const scrollToNext = () => {
  const nextSection = document.querySelector('#services') || document.querySelector('main')
  if (nextSection) {
    nextSection.scrollIntoView({ behavior: 'smooth' })
  }
}

const typeText = (text, speed = 50) => {
  displayedSliderText.value = ''
  let index = 0
  
  const typeInterval = setInterval(() => {
    if (index < text.length) {
      displayedSliderText.value += text[index]
      index++
    } else {
      clearInterval(typeInterval)
      // Wait 5 seconds, then repeat the typing animation
      setTimeout(() => {
        typeText(text, speed)
      }, 5000)
    }
  }, speed)
}

onMounted(() => {
  // Ensure video plays when mounted
  if (backgroundVideoRef.value) {
    // Set video source and try to play
    const playPromise = backgroundVideoRef.value.play()
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.log('Video autoplay prevented:', error)
        // Some browsers require user interaction before playing
      })
    }
  }

  // Hero section animation
  textReveal(heroContent.value, 500)
  
  // Show slider text immediately and start typing animation
  showSliderText.value = true
  const sliderText = t('hero.sliderText')
  typeText(sliderText, 70) // 70ms per character for slower, more readable typing effect
})
</script>

<style scoped>
.hero-section {
  height: 100vh;
  cursor: none;
  position: relative;
}
.custom-cursor {
  position: fixed;
  left: 0;
  top: 0;
  width: 18px;
  height: 18px;
  pointer-events: none;
  border-radius: 50%;
  background: #008067;
  box-shadow: 0 0 16px 6px rgba(0,128,103,0.25), 0 0 0 2px #fff;
  transform: translate(-50%, -50%);
  z-index: 9999;
  animation: cursor-hue-rotate 1s linear infinite;
  transition: background 0.2s, box-shadow 0.2s;
}
@keyframes cursor-hue-rotate {
  0% {
    filter: hue-rotate(0deg);
    box-shadow: 0 0 16px 6px rgba(0,128,103,0.25), 0 0 0 2px #fff;
  }
  25% {
    filter: hue-rotate(90deg);
    box-shadow: 0 0 16px 6px rgba(236,72,153,0.25), 0 0 0 2px #fff;
  }
  50% {
    filter: hue-rotate(180deg);
    box-shadow: 0 0 16px 6px rgba(59,130,246,0.25), 0 0 0 2px #fff;
  }
  75% {
    filter: hue-rotate(270deg);
    box-shadow: 0 0 16px 6px rgba(251,191,36,0.25), 0 0 0 2px #fff;
  }
  100% {
    filter: hue-rotate(360deg);
    box-shadow: 0 0 16px 6px rgba(0,128,103,0.25), 0 0 0 2px #fff;
  }
}
.hero-title {
  @apply text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-6 leading-tight;
}

.hero-subtitle {
  @apply text-lg sm:text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-8 leading-relaxed text-justify;
}

.scroll-indicator {
  @apply w-6 h-10 border-2 border-white rounded-full flex items-center justify-center;
}

.scroll-indicator::before {
  content: '';
  @apply w-1 h-3 bg-white rounded-full animate-bounce;
}

.animate-bounce-slow {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.btn-primary {
  @apply bg-primary text-white hover:bg-primary-dark focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200;
}

.hero-slider-text {
  min-height: 1.5em; /* Prevent layout shift during typing */
}

.typing-cursor {
  display: inline-block;
  margin-left: 2px;
  animation: blink 1s infinite;
  color: #008067;
  font-weight: bold;
}

@keyframes blink {
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
}

/* Scroll Indicator on Left Side */
.scroll-indicator-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.scroll-indicator-left:hover {
  opacity: 1;
}

.scroll-indicator-line {
  width: 2px;
  height: 60px;
  background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.8));
  animation: scroll-line 2s ease-in-out infinite;
}

.scroll-indicator-arrow {
  animation: scroll-bounce 2s ease-in-out infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.scroll-indicator-arrow:hover {
  background: rgba(0, 128, 103, 0.3);
  border-color: rgba(0, 128, 103, 0.5);
  transform: scale(1.1);
}

.scroll-indicator-arrow svg {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

@keyframes scroll-line {
  0%, 100% {
    opacity: 0.3;
    transform: scaleY(0.8);
  }
  50% {
    opacity: 1;
    transform: scaleY(1);
  }
}

@keyframes scroll-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(10px);
  }
}

/* Hide scroll indicator on mobile */
@media (max-width: 768px) {
  .scroll-indicator-left {
    display: none;
  }
}
</style> 